// playbackPreference.ts

/**
 * The speed a clip opens at, for one reader.
 *
 * Three sources, most specific first:
 *
 *   1. a speed saved against THIS clip — someone chose it deliberately for this loop, and
 *      it is shared, so it stands for everyone;
 *   2. this user's own default, set on their profile;
 *   3. the viewer's built-in behaviour, which slows Doppler loops and leaves the rest at
 *      normal speed.
 *
 * The per-user default exists because (1) and (3) were the only options: a sonographer
 * who wanted full speed had to change every loop by hand, and changing it changed what
 * the next reader saw. Nikhil, 9 Sep: "for universal speed, maybe in a settings button
 * somewhere for their login".
 */

/** Speeds offered in the viewer and on the profile. Keep the two lists identical. */
export const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.5, 2] as const;

/** Doppler loops are read frame-by-frame; half speed is what the cardiologists asked for. */
export const DOPPLER_PLAYBACK_RATE = 0.5;

export const NORMAL_PLAYBACK_RATE = 1;

/** A stored preference is only honoured if it is one we offer. */
export function isValidPlaybackRate(value: unknown): value is number {
  return typeof value === 'number' && (PLAYBACK_SPEEDS as readonly number[]).includes(value);
}

/**
 * @param savedForClip  speed stored against this clip, if any
 * @param userDefault   this user's profile preference, if set
 * @param isDoppler     whether the clip is a Doppler loop
 */
export function resolvePlaybackRate(
  savedForClip: number | undefined,
  userDefault: unknown,
  isDoppler: boolean
): number {
  if (typeof savedForClip === 'number' && savedForClip > 0) {
    return savedForClip;
  }
  if (isValidPlaybackRate(userDefault)) {
    // Deliberately overrides the Doppler rule: the user asked for one speed everywhere,
    // and a preference that silently did not apply to Doppler would look broken.
    return userDefault;
  }
  return isDoppler ? DOPPLER_PLAYBACK_RATE : NORMAL_PLAYBACK_RATE;
}
